import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import ms from 'ms';
import { z } from 'zod';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import { queryKeysCompany } from './queryKeys';

const zodCompanyApiKeySchema = z.object({
  createdAt: z.number().int(), // unix timestamp
  key: z.string(),
});

/**
 * `ApiKeysReturn` in the OpenAPI spec — the raw envelope the API returns.
 * `fetchCompanyApiKeys` unwraps this to the `apiKeys` array below, which is
 * what the query actually resolves to.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/get_api_keys_vadmin_company__company_id__api_key_get
 */
const zodCompanyApiKeysEnvelopeSchema = z.object({
  apiKeys: z.array(zodCompanyApiKeySchema),
});

export type CompanyApiKey = z.infer<typeof zodCompanyApiKeySchema>;

export type CompanyApiKeysResponse = CompanyApiKey[] | undefined;

/**
 * Fetches all API keys for a company from the API.
 *
 * @param companyId - The ID of the company to fetch API keys for.
 * @returns Array of API key objects.
 * @throws Error if the API request fails.
 */
export const fetchCompanyApiKeys = async (
  companyId: UUID,
): Promise<CompanyApiKeysResponse> => {
  try {
    const response = await vestigasApi
      .get(ENDPOINT.COMPANY.API_KEY.GET_ALL(companyId), {
        validate: {
          // API keys are secrets; never send them to Sentry breadcrumbs/logs.
          isResponseSensitive: true,
          response: zodCompanyApiKeysEnvelopeSchema,
        },
      })
      .json<z.infer<typeof zodCompanyApiKeysEnvelopeSchema>>();

    return response?.apiKeys;
  } catch (error) {
    log.error({
      message: 'Error fetching API keys for company',
      error,
    });

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};

/**
 * React Query hook for fetching all API keys for a company.
 *
 * @param companyId - The ID of the company to fetch API keys for.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 */
export function useQueryCompanyApiKeys(
  companyId: UUID,
  options?: Omit<
    UseQueryOptions<CompanyApiKeysResponse>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<CompanyApiKeysResponse>({
    enabled: Boolean(companyId),
    queryFn: async () => fetchCompanyApiKeys(companyId),
    queryKey: queryKeysCompany.getApiKeys(companyId),
    staleTime: ms('60s'), // prevent hitting the rate limit
    ...options,
  });
}
